/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the collapsibletable component
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * React
 */
import * as React from 'react';

import {
    makeStyles,
    Theme,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip,
    CircularProgress
} from '@material-ui/core';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { faChevronDown } from '@fortawesome/pro-duotone-svg-icons/faChevronDown';

import { default as pluralize } from 'pluralize';
/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

/*
 * Used to create a table
 */
import Table, { ITableProps } from './Table';

/*
 * ---------------------------------------------------------------------------------
 * interfaces
 * ---------------------------------------------------------------------------------
 */

export interface ICollapsibleTableProps<Type extends object = any> extends ITableProps<Type> {
    title: string;
    loading: boolean;
    entityName?: string;
    startCollapsed?: boolean;
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const useStyles = makeStyles<Theme>(theme => ({
    expansionPanelContainer: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2)
    },
    header: {
        background: theme.palette.primary.main,
        color: theme.palette.common.white,

        '&.Mui-expanded': {
            minHeight: 48
        },

        '& .MuiAccordionSummary-content': {
            justifyContent: 'space-between',

            '&.Mui-expanded': {
                marginTop: theme.spacing(1.5),
                marginBottom: theme.spacing(1.5)
            }
        }
    },
    expand: {
        color: theme.palette.common.white
    },
    expandContent: {
        padding: theme.spacing(0)
    },
    tableWrapper: {
        overflow: 'auto',
        width: '100%'
    }
}));

const usePaginationStyles = makeStyles<Theme>(theme => ({
    root: {
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1)
    }
}));

/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */

const CollapsibleTable = <Type extends object>({
    title,
    loading,
    data,
    entityName,
    options,
    startCollapsed,
    ...tableProps
}: ICollapsibleTableProps<Type>) => {
    const disabled = !loading && (!data || data.length === 0);

    const [expanded, setExpanded] = React.useState(startCollapsed !== true);

    const classes = useStyles();

    const paginationClasses = usePaginationStyles();

    const onClick = React.useCallback(() => {
        setExpanded(!expanded)
    }, [expanded, setExpanded]);

    const upperCasePlural = pluralize.plural(title);

    const lowerCasePlural = upperCasePlural.toLowerCase();

    const entityNameToUse = entityName ?
        entityName :
        title;

    const optionsToUse = React.useMemo(() => {
        return {
            ...options,
            toolbar: options?.toolbar ?? false,
            paging: options?.paging ?? data.length > (options?.pageSizeOptions?.find(p => true) ?? 5)
        }
    }, [options, data])

    return (
        <>
            <Accordion
                disabled={disabled}
                expanded={expanded && !disabled && !loading}
                className={classes.expansionPanelContainer}
            >
                <AccordionSummary
                    onClick={onClick}
                    expandIcon={loading ? <CircularProgress color="secondary" size={16} /> : disabled ? false : <FontAwesomeIcon className={classes.expand} icon={faChevronDown} fixedWidth />}
                    aria-controls={`${title}-content`}
                    id="`${title}-header`"
                    className={classes.header}
                >
                    <Typography>
                        {upperCasePlural}
                    </Typography>
                    {
                        disabled && (
                            <Typography>
                                There are no {lowerCasePlural}
                            </Typography>
                        )
                    }
                    {
                        !loading && data && !disabled && (
                            <Chip
                                label={`${pluralize(entityNameToUse, data.length, true)}`}
                                color="secondary"
                                size="small"
                            />
                        )
                    }
                </AccordionSummary>
                <AccordionDetails
                    className={classes.expandContent}
                >
                    <div
                        className={classes.tableWrapper}
                    >
                        <Table
                            data={data}
                            options={optionsToUse}
                            {...tableProps}
                        />
                    </div>
                </AccordionDetails>
            </Accordion>
        </>
    );
};

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */

export default CollapsibleTable;
